import React, { useState, useEffect, useCallback, useRef, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../lib/fetchWithAuth.js';
import { Logo } from '../components/Logo.js';
import { useThemeContext } from '../hooks/ThemeContext.js';

const API = import.meta.env.VITE_API_URL;

interface Device {
  id: string;
  name: string;
  status: string;
  last_seen: string | null;
  android_version: string | null;
  app_version: string | null;
  version_code?: number | null;
  model: string | null;
  manufacturer: string | null;
  battery_level?: number | null;
  charging?: number | null;
  queue_size?: number | null;
  uploaded_messages?: number | null;
  failed_uploads?: number | null;
  last_successful_sync?: string | null;
  sync_state?: string | null;
  git_hash?: string | null;
  build_date?: string | null;
}

interface AppVersionInfo {
  latestVersion: string;
  minimumSupportedVersion: string;
}

function compareVersions(v1: string, v2: string): number {
  const p1 = (v1.split('-')[0] || '').split('.').map(Number);
  const p2 = (v2.split('-')[0] || '').split('.').map(Number);
  for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
    const num1 = p1[i] || 0;
    const num2 = p2[i] || 0;
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  return 0;
}

interface PairModal {
  code: string;
  expiresAt: string;
  polling: boolean;
}

interface Message {
  id: string;
  sender_number: string;
  body: string;
  timestamp: string;
  has_otp: boolean;
  extracted_otp?: string;
  type?: number;
  isOptimistic?: boolean;
  status?: 'queued' | 'sending' | 'sent' | 'failed';
  error?: string;
}

function renderSyncState(state: string | null | undefined, isOnline: boolean): ReactElement {
  if (!isOnline) return <span style={{ color: 'var(--text-muted)' }}>● Offline</span>;
  switch (state) {
    case 'syncing':
      return <span style={{ color: 'var(--accent-primary)' }}>● Syncing...</span>;
    case 'uploading':
      return <span style={{ color: 'var(--status-warning)' }}>● Uploading</span>;
    case 'importing':
      return <span style={{ color: 'var(--accent-primary)' }}>● Importing</span>;
    case 'waiting':
      return <span style={{ color: 'var(--status-warning)' }}>● Waiting</span>;
    case 'idle':
      return <span style={{ color: 'var(--status-success)' }}>● Idle</span>;
    case 'error':
      return <span style={{ color: 'var(--status-error)' }}>● Error</span>;
    default:
      return <span style={{ color: 'var(--status-success)' }}>● Idle</span>;
  }
}

/** Message and thread timestamps are epoch millis stored as strings; older rows may be ISO strings. */
function tsMs(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isNaN(n) ? Date.parse(String(value)) || 0 : n;
}

function formatShortTime(value: string | number | null | undefined, withTime = false): string {
  const t = tsMs(value);
  if (!t) return '';
  const d = new Date(t);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
  return withTime
    ? `${date}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : date;
}

/** Best-effort OTP from a preview line, for the copy chip in the conversation list. */
function extractOtp(text: string | null): string | null {
  if (!text) return null;
  const match = text.match(/(?:^|[^\d])(\d{4,8})(?!\d)/);
  return match?.[1] ?? null;
}

function initials(name: string): string {
  const letters = name.replace(/[^\p{L}\p{N} ]/gu, ' ').trim();
  if (!letters) return '#';
  if (/^[+\d ]+$/.test(letters)) return '#';
  const parts = letters.split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[1]?.[0] ?? '') : '')).toUpperCase();
}

interface Thread {
  id: string;
  contact_number: string;
  contact_name: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  last_has_otp: boolean;
  unread_count: number;
}

interface DeviceCommand {
  id: string;
  command: string;
  created_at: string;
  completed_at: string | null;
  status: string;
  result: string | null;
  error: string | null;
}

const CMD_LABELS: Record<string, string> = {
  FORCE_FULL_RESYNC: 'Force Full Resync',
  REQUEST_DIAGNOSTICS: 'Diagnostics',
  REQUEST_NATIVE_DIAGNOSTICS: 'Diagnostics',
  CHECK_FOR_UPDATE: 'Update Device',
  DELETE_CONVERSATION: 'Delete Conversation',
  DELETE_MESSAGE: 'Delete Message',
};

function RecentCommands({
  deviceId,
  navigate,
  refreshTrigger,
}: {
  deviceId: string;
  navigate: ReturnType<typeof useNavigate>;
  refreshTrigger?: number | undefined;
}): ReactElement {
  const [commands, setCommands] = useState<DeviceCommand[]>([]);

  useEffect(() => {
    let active = true;
    const fetchCmds = async (): Promise<void> => {
      try {
        const res = await fetchWithAuth(`${API}/devices/${deviceId}/commands`, {}, navigate);
        if (res.ok) {
          const { data } = await res.json();
          if (active) setCommands(data.commands ?? []);
        }
      } catch {
        // Handle error silently
      }
    };

    void fetchCmds();
    return () => {
      active = false;
    };
  }, [deviceId, navigate, refreshTrigger]);

  if (commands.length === 0) return <></>;

  return (
    <div
      style={{
        marginTop: '12px',
        background: 'var(--bg-surface-hover)',
        borderRadius: '4px',
        padding: '8px',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          marginBottom: '8px',
          letterSpacing: '0.5px',
        }}
      >
        Recent Commands
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {commands.slice(0, 5).map((c) => {
          let duration = '';
          if (c.completed_at) {
            const ms = new Date(c.completed_at).getTime() - new Date(c.created_at).getTime();
            duration = `${Math.round(ms / 1000)} seconds`;
          }
          let icon = '⏳';
          let color = 'var(--text-secondary)';
          let statusText = c.status.charAt(0).toUpperCase() + c.status.slice(1);

          if (c.status === 'completed') {
            icon = '✓';
            color = 'var(--status-success)';
          } else if (c.status === 'failed') {
            icon = '✗';
            color = 'var(--status-error)';
          } else if (c.status === 'pending') {
            statusText = 'Delivered';
          } // In Velinko, pending basically means it's queued/delivered until device processes it
          else if (c.status === 'in_progress') {
            icon = '🔄';
            color = 'var(--accent-primary)';
            statusText = 'In Progress';
          }

          if (c.result) {
            try {
              const resObj = JSON.parse(c.result);
              if (resObj.error) {
                statusText = resObj.error;
              } else if (resObj.message) {
                statusText = resObj.message;
              }
            } catch {
              // ignore
            }
          }

          const label = CMD_LABELS[c.command] || c.command;

          return (
            <div
              key={c.id}
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-primary)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>
                  {icon} {label}
                </span>
                <span style={{ color, fontSize: '0.65rem' }}>{statusText}</span>
              </div>
              {duration && (
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {duration}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DashboardPage(): ReactElement {
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeContext();
  const [activeDevice, setActiveDevice] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [appVersionInfo, setAppVersionInfo] = useState<AppVersionInfo | null>(null);

  const [threads, setThreads] = useState<Thread[]>([]);
  const [threadsOffset, setThreadsOffset] = useState(0);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [loadingThreads, setLoadingThreads] = useState(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    messageId: string | null;
  } | null>(null);

  const [pairModal, setPairModal] = useState<PairModal | null>(null);
  const [pairError, setPairError] = useState<string | null>(null);
  const [renameDeviceId, setRenameDeviceId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');
  const [deleteDeviceId, setDeleteDeviceId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const [toastMessage, setToastMessage] = useState<{
    text: string;
    type: 'success' | 'error';
  } | null>(null);
  const [commandRefresh, setCommandRefresh] = useState(0);

  const [executingActions, setExecutingActions] = useState<Record<string, boolean>>({});
  const [mobileView, setMobileView] = useState<'sidebar' | 'threads' | 'messages'>('sidebar');

  const [composerText, setComposerText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // New Message composer state
  const [newMessageMode, setNewMessageMode] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [newBody, setNewBody] = useState('');
  const [sendingNewMessage, setSendingNewMessage] = useState(false);
  const [newMessageResult, setNewMessageResult] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const setActionLoading = (deviceId: string, action: string, isLoading: boolean): void => {
    setExecutingActions((prev) => ({ ...prev, [`${deviceId}_${action}`]: isLoading }));
  };
  const isActionLoading = (deviceId: string, action: string): boolean => {
    return !!executingActions[`${deviceId}_${action}`];
  };

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const getToken = (): string | null => localStorage.getItem('velinko_token');

  const fetchDevices = useCallback(async (): Promise<void> => {
    const tokenStr = getToken();
    if (!tokenStr) {
      navigate('/login');
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/devices`, {}, navigate);
      if (res.ok) {
        const { data } = await res.json();
        setDevices((prevDevices) => {
          const fetchedDevices = data.devices ?? [];
          const onlineDeviceIds = new Set(
            prevDevices.filter((d) => d.status === 'online').map((d) => d.id),
          );
          return fetchedDevices.map((d: Device) =>
            onlineDeviceIds.has(d.id) ? { ...d, status: 'online' } : d,
          );
        });
      } else if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('velinko_token');
        navigate('/login');
      }
    } catch (err) {
      console.error('Failed to fetch devices', err);
    }
  }, [navigate]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDevices();

    fetch(`${API}/app/version`)
      .then((r) => r.json())
      .then((d) => setAppVersionInfo(d))
      .catch(() => {});
  }, [navigate, fetchDevices]);

  // Fetch Threads
  const fetchThreads = useCallback(
    async (reset = false): Promise<void> => {
      if (!activeDevice) return;
      const tokenStr = getToken();
      if (!tokenStr) {
        navigate('/login');
        return;
      }
      const currentOffset = reset ? 0 : threadsOffset;
      if (reset) setLoadingThreads(true);
      try {
        const q = searchQuery ? `&q=${encodeURIComponent(searchQuery)}` : '';
        const arch = showArchived ? '&archived=true' : '';
        const res = await fetchWithAuth(
          `${API}/sms/threads?deviceId=${activeDevice}&limit=50&offset=${currentOffset}${q}${arch}`,
          {},
          navigate,
        );
        if (res.ok) {
          const { data } = await res.json();
          if (reset) {
            setThreads((prev) => {
              const newThreads = data.threads ?? [];
              const merged = [...newThreads];
              const newIds = new Set(newThreads.map((t: Thread) => t.id));
              for (const t of prev) {
                if (!newIds.has(t.id)) merged.push(t);
              }
              return merged.sort((a, b) => tsMs(b.last_message_at) - tsMs(a.last_message_at));
            });
          } else {
            setThreads((prev) => [...prev, ...(data.threads ?? [])]);
          }
          if (!reset) {
            setThreadsOffset(currentOffset + (data.threads?.length ?? 0));
          }
          setHasMoreThreads((data.threads?.length ?? 0) === 50);
        } else if (res.status === 401) {
          navigate('/login');
        }
      } catch (err) {
        console.error('Failed to fetch threads', err);
      } finally {
        if (reset) setLoadingThreads(false);
      }
    },
    [activeDevice, searchQuery, threadsOffset, navigate, showArchived],
  );

  useEffect(() => {
    if (activeDevice) fetchThreads(true);
  }, [activeDevice, searchQuery, fetchThreads]);

  // Fetch Messages for active thread
  const fetchMessages = useCallback(
    async (reset = false): Promise<void> => {
      if (!activeDevice || !activeThreadId) return;
      const tokenStr = getToken();
      if (!tokenStr) {
        navigate('/login');
        return;
      }
      const currentOffset = reset ? 0 : messagesOffset;
      if (reset) setLoadingMessages(true);
      try {
        const arch = showArchived ? '&archived=true' : '';
        const res = await fetchWithAuth(
          `${API}/sms?deviceId=${activeDevice}&threadId=${activeThreadId}&limit=50&offset=${currentOffset}${arch}`,
          {},
          navigate,
        );
        if (res.ok) {
          const { data } = await res.json();
          if (reset) {
            setMessages((prev) => {
              const newMessages = data.messages ?? [];
              const merged = [...newMessages];
              const newIds = new Set(newMessages.map((m: Message) => m.id));
              const oldestNewMsgTime =
                newMessages.length > 0
                  ? Math.min(...newMessages.map((m: Message) => tsMs(m.timestamp)))
                  : 0;

              const reconciledCanonicalIds = new Set<string>();

              for (const m of prev) {
                if (!newIds.has(m.id)) {
                  // Keep older messages from pagination. Drop deleted messages in the current polling window.
                  if (tsMs(m.timestamp) < oldestNewMsgTime) {
                    merged.push(m);
                  } else if (m.isOptimistic) {
                    // Reconcile optimistic messages with newly fetched canonical ones
                    const reconciled = newMessages.find(
                      (newM: Message) =>
                        !reconciledCanonicalIds.has(newM.id) &&
                        newM.type === 2 &&
                        newM.body === m.body &&
                        Math.abs(Number(newM.timestamp) - Number(m.timestamp)) < 120000, // within 2 minutes
                    );
                    if (!reconciled) {
                      merged.push(m);
                    } else {
                      reconciledCanonicalIds.add(reconciled.id);
                      // Optimistic message is dropped (reconciled).
                      // The canonical message `newM` is already included in `merged` via `[...newMessages]`.
                    }
                  }
                }
              }
              // Stored newest-first like the API returns them; displayMessages reverses for rendering.
              return merged.sort((a, b) => tsMs(b.timestamp) - tsMs(a.timestamp));
            });
          } else {
            setMessages((prev) => [...prev, ...(data.messages ?? [])]);
          }
          if (!reset) {
            setMessagesOffset(currentOffset + (data.messages?.length ?? 0));
          }
          setHasMoreMessages((data.messages?.length ?? 0) === 50);
        }
      } catch (err) {
        console.error('Failed to fetch messages', err);
      } finally {
        if (reset) setLoadingMessages(false);
      }
    },
    [activeDevice, activeThreadId, messagesOffset, navigate, showArchived],
  );

  useEffect(() => {
    if (activeThreadId) {
      setMessages([]);
      fetchMessages(true);
    } else {
      setMessages([]);
    }
  }, [activeThreadId, fetchMessages]);

  useEffect(() => {
    if (!activeDevice) {
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const connect = (): void => {
      const token = getToken();
      if (!token) return;

      const wsUrl = API.replace(/^http/, 'ws') + `/devices/${activeDevice}/live`;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        ws?.send(
          JSON.stringify({
            type: 'auth',
            role: 'dashboard',
            token,
            deviceId: activeDevice,
          }),
        );
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'presence') {
            setDevices((prev) =>
              prev.map((d) =>
                d.id === (data.deviceId || activeDevice)
                  ? {
                      ...d,
                      status: data.online ? 'online' : 'offline',
                      last_seen: data.online ? new Date().toISOString() : d.last_seen,
                      app_version: data.appVersion || d.app_version,
                      version_code: data.versionCode || d.version_code,
                    }
                  : d,
              ),
            );
          } else if (data.type === 'device_online') {
            setDevices((prev) =>
              prev.map((d) =>
                d.id === (data.deviceId || activeDevice)
                  ? {
                      ...d,
                      status: 'online',
                      last_seen: new Date().toISOString(),
                      app_version: data.appVersion || d.app_version,
                      version_code: data.versionCode || d.version_code,
                    }
                  : d,
              ),
            );
            fetchThreads(true); // Fetch threads in case missed
          } else if (data.type === 'device_offline') {
            setDevices((prev) =>
              prev.map((d) =>
                d.id === (data.deviceId || activeDevice) ? { ...d, status: 'offline' } : d,
              ),
            );
          } else if (data.type === 'command_ack') {
            setMessages((prev) => {
              const newMsgs = [...prev];
              const idx = newMsgs.findIndex((m) => m.id === data.commandId);
              if (idx !== -1) {
                const msg = newMsgs[idx];
                if (msg) {
                  let nextStatus = msg.status;
                  let error = msg.error;
                  if (data.status === 'in_progress') nextStatus = 'sending';
                  else if (data.status === 'success') nextStatus = 'sent';
                  else if (data.status === 'failure') {
                    nextStatus = 'failed';
                    error = data.payload?.error || 'Failed';
                  }
                  const updatedMsg: Message = { ...msg };
                  if (nextStatus) updatedMsg.status = nextStatus;
                  if (error !== undefined) updatedMsg.error = error;
                  newMsgs[idx] = updatedMsg;
                }
              }
              return newMsgs;
            });
            setCommandRefresh((r) => r + 1);
          } else if (data.type === 'command_completed' || data.type === 'sms_synced') {
            // refresh data
            fetchThreads(true);
            if (activeThreadId) fetchMessages(true);
            setCommandRefresh((r) => r + 1);
          }
        } catch (e) {
          console.error('Failed to parse WS message', e);
        }
      };

      ws.onclose = (event) => {
        if (!isMounted) return;
        if (event.code === 4011) {
          // The access token expired. Any authed request refreshes it, then reconnect.
          fetchWithAuth(`${API}/devices`, {}, navigate)
            .catch(() => undefined)
            .finally(() => {
              if (isMounted) reconnectTimer = setTimeout(connect, 1000);
            });
          return;
        }
        reconnectTimer = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      isMounted = false;
      clearTimeout(reconnectTimer);
      if (ws) {
        ws.close();
      }
    };
  }, [activeDevice, activeThreadId, fetchThreads, fetchMessages, navigate]);

  useEffect(() => {
    if (!pairModal?.polling) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetchWithAuth(`${API}/pair/${pairModal.code}/status`, {}, navigate);
        if (res.ok) {
          const { data } = await res.json();
          if (data.status === 'claimed') {
            clearInterval(interval);
            setPairModal(null);
            await fetchDevices();
          }
        }
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [pairModal, fetchDevices, navigate]);

  const handlePairDevice = async (): Promise<void> => {
    setPairError(null);
    try {
      const res = await fetchWithAuth(
        `${API}/pair/create`,
        {
          method: 'POST',
        },
        navigate,
      );
      if (res.ok) {
        const { data } = await res.json();
        setPairModal({ code: data.code, expiresAt: data.expiresAt, polling: true });
      } else {
        setPairError('Failed to create pairing code');
      }
    } catch {
      setPairError('Network error');
    }
  };

  const handleRenameDevice = async (deviceId: string, newName: string): Promise<void> => {
    try {
      // Optimistic update
      setDevices((prev) => prev.map((d) => (d.id === deviceId ? { ...d, name: newName } : d)));
      setRenameDeviceId(null);

      const res = await fetchWithAuth(
        `${API}/devices/${deviceId}/name`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName }),
        },
        navigate,
      );
      if (!res.ok) {
        // Revert on failure (simplified)
        await fetchDevices();
      }
    } catch (err) {
      console.error('Rename failed', err);
    }
  };

  const handleDeleteDevice = async (deviceId: string): Promise<void> => {
    try {
      const res = await fetchWithAuth(
        `${API}/devices/${deviceId}`,
        {
          method: 'DELETE',
        },
        navigate,
      );
      if (res.ok) {
        setDevices((prev) => prev.filter((d) => d.id !== deviceId));
        if (activeDevice === deviceId) {
          setActiveDevice(null);
          setActiveThreadId(null);
        }
        await fetchDevices();
      }
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleUpdateDevice = async (deviceId: string): Promise<void> => {
    setActionLoading(deviceId, 'update', true);
    try {
      const res = await fetchWithAuth(
        `${API}/devices/${deviceId}/command/check-for-update`,
        { method: 'POST' },
        navigate,
      );
      if (res.ok) {
        showToast('✓ Command queued', 'success');
        setTimeout(fetchDevices, 2000);
      } else {
        showToast('✗ Failed to queue command', 'error');
      }
    } catch (err) {
      console.error('Update failed', err);
      showToast('✗ Failed to queue command', 'error');
    } finally {
      setActionLoading(deviceId, 'update', false);
    }
  };

  const handleForceSync = async (deviceId: string): Promise<void> => {
    setActionLoading(deviceId, 'forcesync', true);
    try {
      const res = await fetchWithAuth(
        `${API}/devices/${deviceId}/command/force-sync`,
        { method: 'POST' },
        navigate,
      );
      if (res.ok) {
        showToast('✓ Command queued', 'success');
        setTimeout(fetchDevices, 2000);
      } else {
        showToast('✗ Failed to queue command', 'error');
      }
    } catch (err) {
      console.error('Force sync failed', err);
      showToast('✗ Failed to queue command', 'error');
    } finally {
      setActionLoading(deviceId, 'forcesync', false);
    }
  };

  const handleRequestDiagnostics = async (deviceId: string): Promise<void> => {
    setActionLoading(deviceId, 'diagnostics', true);
    try {
      const res = await fetchWithAuth(
        `${API}/devices/${deviceId}/command/diagnostics`,
        { method: 'POST' },
        navigate,
      );
      if (res.ok) {
        showToast('✓ Command queued', 'success');
        setTimeout(fetchDevices, 2000);
      } else {
        showToast('✗ Failed to queue command', 'error');
      }
    } catch (err) {
      console.error('Request diagnostics failed', err);
      showToast('✗ Failed to queue command', 'error');
    } finally {
      setActionLoading(deviceId, 'diagnostics', false);
    }
  };

  const handleBulkAction = useCallback(
    async (
      action: 'delete' | 'archive' | 'unarchive' | 'read' | 'unread',
      messageIds?: string[],
      threadIds?: string[],
    ): Promise<void> => {
      if (action === 'delete') {
        if (
          !confirm(
            `Delete ${messageIds?.length || 0} messages and ${threadIds?.length || 0} threads?`,
          )
        )
          return;
      }
      try {
        if (messageIds && messageIds.length > 0) {
          if (action === 'delete') {
            setMessages((prev) => prev.filter((m) => !messageIds.includes(m.id)));
          } else if (action === 'archive' || action === 'unarchive') {
            fetchMessages(true); // Refetch to apply filter
          }
        }
        if (threadIds && threadIds.length > 0) {
          if (action === 'delete') {
            setThreads((prev) => prev.filter((t) => !threadIds.includes(t.id)));
            if (activeThreadId && threadIds.includes(activeThreadId)) {
              setActiveThreadId(null);
              setMessages([]);
            }
          } else {
            fetchThreads(true);
          }
        }
        setSelectedMessages(new Set());
        setContextMenu(null);

        await fetchWithAuth(
          `${API}/sms/action`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action, messageIds, threadIds, deviceId: activeDevice }),
          },
          navigate,
        );
      } catch (err) {
        console.error(`Bulk ${action} failed`, err);
      }
    },
    [activeDevice, activeThreadId, navigate, fetchMessages, fetchThreads],
  );

  const handleBulkDelete = useCallback(
    async (messageIds: string[]): Promise<void> => {
      await handleBulkAction('delete', messageIds, []);
    },
    [handleBulkAction],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSelectedMessages(new Set());
        setContextMenu(null);
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'a' && activeThreadId) {
        e.preventDefault();
        setSelectedMessages(new Set(messages.map((m) => m.id)));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedMessages.size > 0 && !renameDeviceId && !pairModal) {
          e.preventDefault();
          handleBulkAction('delete', Array.from(selectedMessages));
        }
      } else if (e.key === 'e') {
        // Archive shortcut
        if (selectedMessages.size > 0 && !renameDeviceId && !pairModal) {
          e.preventDefault();
          handleBulkAction('archive', Array.from(selectedMessages));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [messages, selectedMessages, activeThreadId, renameDeviceId, pairModal, handleBulkAction]);

  const handleDeleteThread = async (threadId: string): Promise<void> => {
    await handleBulkAction('delete', [], [threadId]);
  };

  const selectedThread = threads.find((t) => t.id === activeThreadId);
  const selectedDevice = devices.find((d) => d.id === activeDevice);

  const handleSendMessage = useCallback(async () => {
    if (!composerText.trim() || !activeDevice || !activeThreadId || !selectedThread) return;
    setSendingMessage(true);

    const optimisticId = `opt_${Date.now()}`;
    const sentText = composerText;

    setMessages((prev) => [
      {
        id: optimisticId,
        sender_number: selectedThread.contact_number,
        body: sentText,
        timestamp: Date.now().toString(),
        has_otp: false,
        type: 2,
        isOptimistic: true,
        status: 'queued',
      },
      ...prev,
    ]);

    setComposerText('');

    try {
      const res = await fetchWithAuth(
        `${API}/sms/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId: activeDevice,
            recipient: selectedThread.contact_number,
            body: sentText,
          }),
        },
        navigate,
      );

      if (!res.ok) {
        showToast('Failed to send message', 'error');
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        setComposerText(sentText); // Restore text on failure
      } else {
        const bodyRes = await res.json();
        if (bodyRes.data?.commandId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === optimisticId ? { ...m, id: bodyRes.data.commandId } : m)),
          );
        }
      }
    } catch (err) {
      console.error('Send SMS failed:', err);
      showToast('Network error', 'error');
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setComposerText(sentText);
    } finally {
      setSendingMessage(false);
    }
  }, [
    composerText,
    activeDevice,
    activeThreadId,
    selectedThread,
    navigate,
    showToast,
    selectedDevice?.status,
  ]);

  // New Message handler: send SMS to arbitrary recipient
  const handleNewMessageSend = useCallback(async () => {
    if (!newRecipient.trim() || !newBody.trim() || !activeDevice) return;
    setSendingNewMessage(true);
    setNewMessageResult(null);

    try {
      const res = await fetchWithAuth(
        `${API}/sms/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId: activeDevice,
            recipient: newRecipient.trim(),
            body: newBody.trim(),
          }),
        },
        navigate,
      );

      if (res.ok) {
        const bodyRes = await res.json();
        let cmdId = bodyRes.data?.commandId ?? 'unknown';
        if (typeof cmdId === 'object' && cmdId !== null && 'id' in cmdId) {
          cmdId = cmdId.id;
        }
        setNewMessageResult({ type: 'success', text: `Command queued (${cmdId})` });
        setNewBody('');
        setCommandRefresh((prev) => prev + 1);
        showToast('SMS command queued', 'success');
      } else {
        const errBody = await res.json().catch(() => null);
        const errMsg = errBody?.error?.message || `HTTP ${res.status}`;
        setNewMessageResult({ type: 'error', text: errMsg });
      }
    } catch (err) {
      console.error('New message send failed:', err);
      setNewMessageResult({ type: 'error', text: 'Network error' });
    } finally {
      setSendingNewMessage(false);
    }
  }, [newRecipient, newBody, activeDevice, navigate, showToast]);

  const handleRetryMessage = useCallback((failedMessage: Message) => {
    // Remove the failed optimistic message
    setMessages((prev) => prev.filter((m) => m.id !== failedMessage.id));
    // Put body back into composer so user can re-send (which generates new command ID)
    setComposerText(failedMessage.body);
  }, []);

  const displayMessages = [...messages].reverse();

  const copyOtp = (code: string): void => {
    navigator.clipboard
      .writeText(code)
      .then(() => showToast(`Copied ${code}`))
      .catch(() => showToast('Could not copy to clipboard', 'error'));
  };

  const isDarkTheme =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <div className={`dashboard-layout mobile-view-${mobileView}`}>
      {pairModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ textAlign: 'center' }}>
            <h3 style={{ marginBottom: 6 }}>Pair a device</h3>
            <p style={{ margin: 0, fontSize: 13 }}>
              Open Velinko on your phone and enter this code.
            </p>
            <div className="pair-code">{pairModal.code}</div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 0 }}>
              Waiting for the device · expires {new Date(pairModal.expiresAt).toLocaleTimeString()}
            </p>
            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <button onClick={() => setPairModal(null)} className="btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {renameDeviceId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Rename device</h3>
            <input
              className="input"
              type="text"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              placeholder="New name"
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <div className="modal-actions">
              <button onClick={() => setRenameDeviceId(null)} className="btn">
                Cancel
              </button>
              <button
                onClick={() => handleRenameDevice(renameDeviceId, renameName)}
                disabled={!renameName.trim()}
                className="btn btn-primary"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteDeviceId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete device</h3>
            <p style={{ fontSize: 13.5 }}>
              This unpairs the device and removes its synced messages from your dashboard.
            </p>
            <div className="modal-actions">
              <button onClick={() => setDeleteDeviceId(null)} className="btn">
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteDevice(deleteDeviceId);
                  setDeleteDeviceId(null);
                }}
                className="btn btn-danger-solid"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Devices */}
      <aside className="sidebar">
        <div className="pane-header">
          <span className="brand">
            <Logo size={22} />
            Velinko
          </span>
          <button
            className="icon-btn"
            onClick={() => setTheme(isDarkTheme ? 'light' : 'dark')}
            title={isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-label="Toggle theme"
          >
            {isDarkTheme ? '☀' : '☾'}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px 8px' }}>
          <span className="section-label" style={{ flex: 1 }}>
            Devices
          </span>
          <button
            onClick={handlePairDevice}
            title="Pair new device"
            className="btn btn-primary btn-sm"
          >
            + Pair
          </button>
        </div>
        {pairError && (
          <div style={{ color: 'var(--status-error)', fontSize: 12.5, padding: '0 16px 8px' }}>
            {pairError}
          </div>
        )}
        <div className="pane-scroll">
          {devices.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 16px' }}>
              <h3>No devices yet</h3>
              <p>Choose + Pair, then enter the code in the Velinko app on your phone.</p>
            </div>
          ) : (
            devices.map((d) => {
              const active = activeDevice === d.id;
              // Device actions show on the selected phone (or the only one) to keep the list short.
              return (
                <div
                  key={d.id}
                  className={`device-card ${active ? 'active' : ''}`}
                  onClick={() => {
                    setActiveDevice(d.id);
                    setActiveThreadId(null);
                    setMobileView('threads');
                  }}
                >
                  <div className="device-name">{d.name}</div>
                  <div className="device-meta">
                    {[
                      d.manufacturer && d.model ? `${d.manufacturer} ${d.model}` : d.model,
                      d.android_version ? `Android ${d.android_version}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  <div className="device-meta">
                    v{d.app_version ?? '—'} {d.version_code ? `(${d.version_code})` : ''}
                    {d.git_hash ? ` · ${d.git_hash.substring(0, 7)}` : ''}
                    {d.battery_level !== null && d.battery_level !== undefined
                      ? ` · ${d.battery_level}%${d.charging ? ' ⚡' : ''}`
                      : ''}
                  </div>
                  <div className="device-status">
                    <span>
                      {renderSyncState(d.sync_state, d.status === 'online')} ·{' '}
                      {d.last_seen ? new Date(d.last_seen).toLocaleTimeString() : 'Never seen'}
                    </span>
                    {appVersionInfo && d.app_version ? (
                      compareVersions(d.app_version, appVersionInfo.minimumSupportedVersion) < 0 ? (
                        <span style={{ color: 'var(--status-error)' }}>Update Required</span>
                      ) : compareVersions(d.app_version, appVersionInfo.latestVersion) < 0 ? (
                        <span style={{ color: 'var(--status-warning)' }}>Update Available</span>
                      ) : (
                        <span style={{ color: 'var(--status-success)' }}>Up to date</span>
                      )
                    ) : null}
                  </div>
                  {active || devices.length === 1 ? (
                    <div className="action-grid" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn primary"
                        onClick={() => {
                          if (!isActionLoading(d.id, 'diagnostics')) handleRequestDiagnostics(d.id);
                        }}
                        disabled={isActionLoading(d.id, 'diagnostics')}
                      >
                        {isActionLoading(d.id, 'diagnostics') ? 'Syncing…' : 'Sync Now'}
                      </button>
                      <button
                        className="btn"
                        onClick={() => {
                          if (
                            !isActionLoading(d.id, 'forcesync') &&
                            confirm('Force a full resync of all messages from this device?')
                          ) {
                            handleForceSync(d.id);
                          }
                        }}
                        disabled={isActionLoading(d.id, 'forcesync')}
                      >
                        {isActionLoading(d.id, 'forcesync') ? 'Resyncing…' : 'Force Full Resync'}
                      </button>
                      <button
                        className="btn"
                        onClick={() => {
                          if (!isActionLoading(d.id, 'update')) handleUpdateDevice(d.id);
                        }}
                        disabled={isActionLoading(d.id, 'update')}
                      >
                        {isActionLoading(d.id, 'update') ? 'Updating…' : 'Update Device'}
                      </button>
                      <button
                        className="btn"
                        onClick={() => navigate(`/device/${d.id}/diagnostics`)}
                      >
                        Diagnostics
                      </button>
                      <button
                        className="btn"
                        onClick={() => {
                          setRenameDeviceId(d.id);
                          setRenameName(d.name);
                        }}
                      >
                        Rename
                      </button>
                      <button
                        className="btn danger"
                        onClick={() => setDeleteDeviceId(d.id)}
                        title="Delete Device"
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}
                  {active ? (
                    <RecentCommands
                      deviceId={d.id}
                      navigate={navigate}
                      refreshTrigger={commandRefresh}
                    />
                  ) : null}
                </div>
              );
            })
          )}
        </div>
        <div className="sidebar-footer">
          <div className="build-info">
            <div>v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}</div>
            <div>{typeof __COMMIT_HASH__ !== 'undefined' ? __COMMIT_HASH__ : 'local'}</div>
            <div>
              {typeof __BUILD_TIME__ !== 'undefined'
                ? new Date(__BUILD_TIME__).toLocaleString(undefined, { timeZoneName: 'short' })
                : 'local'}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/docs')}>
            Docs
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/account')}>
            Account
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              localStorage.clear();
              navigate('/login');
            }}
          >
            Log out
          </button>
        </div>
      </aside>

      {/* Conversations */}
      <section className="threads-pane">
        <div className="pane-header">
          <button
            className="icon-btn mobile-only-btn"
            onClick={() => setMobileView('sidebar')}
            title="Back to devices"
          >
            ←
          </button>
          <h2 className="pane-title">{showArchived ? 'Archive' : 'Messages'}</h2>
          <button
            className={`btn btn-ghost btn-sm ${showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'View Inbox' : 'View Archive'}
          </button>
        </div>
        <div className="thread-search">
          <input
            type="text"
            placeholder="Search conversations"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search conversations"
          />
        </div>
        <div className="pane-scroll">
          {!activeDevice ? (
            <div className="empty-state">
              <h3>No device selected</h3>
              <p>Select a device from the sidebar to view its messages.</p>
            </div>
          ) : loadingThreads && threads.length === 0 ? (
            <div>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="thread-item">
                  <div className="avatar skeleton" />
                  <div className="thread-body">
                    <div
                      className="skeleton"
                      style={{ height: 12, width: '55%', marginBottom: 8 }}
                    />
                    <div className="skeleton" style={{ height: 11, width: '90%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : threads.length === 0 ? (
            <div className="empty-state">
              <h3>No messages</h3>
              <p style={{ marginBottom: 16 }}>Nothing has synced from this device yet.</p>
              {!newMessageMode ? (
                <button
                  id="btn-new-message"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setNewMessageMode(true);
                    setNewMessageResult(null);
                  }}
                >
                  New message
                </button>
              ) : (
                <div style={{ textAlign: 'left', width: '100%', maxWidth: 360 }}>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label htmlFor="new-msg-recipient">Recipient</label>
                    <input
                      id="new-msg-recipient"
                      type="tel"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      placeholder="+1234567890"
                      disabled={sendingNewMessage}
                    />
                  </div>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label htmlFor="new-msg-body">Message</label>
                    <textarea
                      id="new-msg-body"
                      value={newBody}
                      onChange={(e) => setNewBody(e.target.value)}
                      placeholder="Type your message"
                      rows={3}
                      disabled={sendingNewMessage}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleNewMessageSend();
                        }
                      }}
                    />
                  </div>
                  {newMessageResult && (
                    <div
                      className={
                        newMessageResult.type === 'success' ? 'auth-success' : 'auth-error'
                      }
                    >
                      {newMessageResult.text}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      id="btn-send-new-msg"
                      className="btn btn-primary btn-sm"
                      style={{ flex: 1 }}
                      onClick={handleNewMessageSend}
                      disabled={sendingNewMessage || !newRecipient.trim() || !newBody.trim()}
                    >
                      {sendingNewMessage ? 'Sending…' : 'Send'}
                    </button>
                    <button
                      id="btn-cancel-new-msg"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setNewMessageMode(false);
                        setNewRecipient('');
                        setNewBody('');
                        setNewMessageResult(null);
                      }}
                      disabled={sendingNewMessage}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {threads.map((thread) => {
                const title = thread.contact_name || thread.contact_number;
                const otp = thread.last_has_otp ? extractOtp(thread.last_message_preview) : null;
                return (
                  <div
                    key={thread.id}
                    onClick={() => {
                      setActiveThreadId(thread.id);
                      setMobileView('messages');
                    }}
                    className={`thread-item ${activeThreadId === thread.id ? 'active' : ''} ${thread.unread_count > 0 ? 'unread' : ''}`}
                  >
                    <div className="avatar">{initials(title)}</div>
                    <div className="thread-body">
                      <div className="thread-top">
                        <span className="thread-name">{title}</span>
                        <span className="thread-time">
                          {formatShortTime(thread.last_message_at)}
                        </span>
                      </div>
                      <div className="thread-preview">{thread.last_message_preview}</div>
                      {otp || thread.unread_count > 0 ? (
                        <div className="thread-row-actions">
                          {otp ? (
                            <button
                              className="otp-chip"
                              title="Copy code"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyOtp(otp);
                              }}
                            >
                              {otp} <span className="hint">Copy</span>
                            </button>
                          ) : null}
                          <span style={{ flex: 1 }} />
                          {thread.unread_count > 0 ? (
                            <span className="unread-dot" title="Unread" />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <button
                      className="thread-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteThread(thread.id);
                      }}
                      title="Delete Conversation"
                      aria-label="Delete conversation"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
              {hasMoreThreads && (
                <button
                  className="btn btn-ghost btn-block"
                  style={{ border: 'none' }}
                  onClick={() => fetchThreads(false)}
                >
                  {loadingThreads ? 'Loading…' : 'Load more'}
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* Messages */}
      <main className="messages-pane">
        {selectedDevice && !selectedThread && (
          <>
            <div className="pane-header">
              <button
                className="icon-btn mobile-only-btn"
                onClick={() => setMobileView('threads')}
                title="Back to messages"
              >
                ←
              </button>
              <h2 className="pane-title">{selectedDevice.name}</h2>
              <span style={{ fontSize: 12.5 }}>
                {renderSyncState(selectedDevice.sync_state, selectedDevice.status === 'online')}
              </span>
            </div>
            <div className="overview">
              <div className="section-label" style={{ marginBottom: 10 }}>
                Device
              </div>
              <div className="stat-grid">
                <div className="device-details-card">
                  <div className="device-details-label">Model</div>
                  <div className="device-details-value" style={{ fontSize: 15 }}>
                    {selectedDevice.manufacturer} {selectedDevice.model}
                  </div>
                </div>
                <div className="device-details-card">
                  <div className="device-details-label">Android</div>
                  <div className="device-details-value">
                    {selectedDevice.android_version ?? '—'}
                  </div>
                </div>
                <div className="device-details-card">
                  <div className="device-details-label">App version</div>
                  <div className="device-details-value">
                    {selectedDevice.app_version ?? '—'}
                    {selectedDevice.version_code ? (
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                        {' '}
                        ({selectedDevice.version_code})
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="device-details-card">
                  <div className="device-details-label">Battery</div>
                  <div className="device-details-value">
                    {selectedDevice.battery_level !== null &&
                    selectedDevice.battery_level !== undefined
                      ? `${selectedDevice.battery_level}%${selectedDevice.charging ? ' ⚡' : ''}`
                      : '—'}
                  </div>
                </div>
              </div>
              <div className="section-label" style={{ marginBottom: 10 }}>
                Sync health
              </div>
              <div className="stat-grid">
                <div className="device-details-card">
                  <div className="device-details-label">State</div>
                  <div className="device-details-value" style={{ textTransform: 'capitalize' }}>
                    {selectedDevice.sync_state || 'Unknown'}
                  </div>
                </div>
                <div className="device-details-card">
                  <div className="device-details-label">Waiting to upload</div>
                  <div className="device-details-value">{selectedDevice.queue_size ?? 0}</div>
                </div>
                <div className="device-details-card">
                  <div className="device-details-label">Uploaded</div>
                  <div className="device-details-value">
                    {selectedDevice.uploaded_messages ?? 0}
                  </div>
                </div>
                <div className="device-details-card">
                  <div className="device-details-label">Failed</div>
                  <div
                    className="device-details-value"
                    style={{
                      color: selectedDevice.failed_uploads ? 'var(--status-error)' : undefined,
                    }}
                  >
                    {selectedDevice.failed_uploads ?? 0}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: 13 }}>Select a conversation to read messages.</p>
            </div>
          </>
        )}
        {selectedThread ? (
          <>
            <div className="pane-header">
              <button
                className="icon-btn mobile-only-btn"
                onClick={() => setMobileView('threads')}
                title="Back to messages"
              >
                ←
              </button>
              <div className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
                {initials(selectedThread.contact_name || selectedThread.contact_number)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="pane-title" style={{ display: 'block' }}>
                  {selectedThread.contact_name || selectedThread.contact_number}
                </h2>
                {selectedThread.contact_name && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {selectedThread.contact_number}
                  </div>
                )}
              </div>
            </div>

            <div className="message-list" onClick={() => setContextMenu(null)}>
              {selectedMessages.size > 0 && (
                <div className="selection-bar">
                  <strong>{selectedMessages.size} selected</strong>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" onClick={() => setSelectedMessages(new Set())}>
                      Clear
                    </button>
                    <button
                      className="btn btn-sm btn-danger-solid"
                      onClick={() => handleBulkDelete(Array.from(selectedMessages))}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}

              {loadingMessages && messages.length === 0 && (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 'auto' }}
                >
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: i % 2 === 0 ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <div
                        className="skeleton"
                        style={{ height: 40, width: '55%', maxWidth: 260, borderRadius: 14 }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {hasMoreMessages && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ alignSelf: 'center', borderRadius: 999 }}
                  onClick={() => fetchMessages(false)}
                >
                  {loadingMessages ? 'Loading…' : 'Load older messages'}
                </button>
              )}
              {displayMessages.map((m) => {
                const isIncoming = m.type !== 2;
                const isSelected = selectedMessages.has(m.id);
                return (
                  <div
                    key={m.id}
                    className={`message-bubble-container ${isIncoming ? 'incoming' : 'outgoing'}`}
                    style={{ opacity: m.isOptimistic ? 0.7 : 1 }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({ x: e.clientX, y: e.clientY, messageId: m.id });
                    }}
                  >
                    <div className="message-row">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        aria-label="Select message"
                        onChange={(e) => {
                          const newSet = new Set(selectedMessages);
                          if (e.target.checked) newSet.add(m.id);
                          else newSet.delete(m.id);
                          setSelectedMessages(newSet);
                        }}
                      />
                      <div
                        className={`message-bubble ${isIncoming ? 'incoming' : 'outgoing'} ${isSelected ? 'selected' : ''}`}
                      >
                        <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                        {m.has_otp && m.extracted_otp ? (
                          <div className="otp-box">
                            <span className="otp-text">{m.extracted_otp}</span>
                            <button
                              onClick={() => copyOtp(m.extracted_otp!)}
                              className="btn btn-primary btn-sm"
                            >
                              Copy
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="message-meta">
                      <span title={new Date(tsMs(m.timestamp)).toLocaleString()}>
                        {formatShortTime(m.timestamp, true)}
                      </span>
                      {m.isOptimistic && m.status && (
                        <span
                          style={{
                            color:
                              m.status === 'failed'
                                ? 'var(--status-error)'
                                : m.status === 'sent'
                                  ? 'var(--status-success)'
                                  : 'var(--text-secondary)',
                          }}
                        >
                          {m.status === 'queued' &&
                            (selectedDevice?.status === 'offline'
                              ? 'Queued, waiting for device'
                              : 'Queued')}
                          {m.status === 'sending' && 'Sending…'}
                          {m.status === 'sent' && 'Sent ✓'}
                          {m.status === 'failed' && `Failed: ${m.error || 'Unknown'}`}
                          {m.status === 'failed' && (
                            <button
                              onClick={() => handleRetryMessage(m)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--accent-primary)',
                                fontSize: 'inherit',
                                cursor: 'pointer',
                                marginLeft: 6,
                                padding: 0,
                              }}
                            >
                              Retry
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {contextMenu && (
              <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
                <button
                  onClick={() => {
                    if (contextMenu.messageId) {
                      const newSet = new Set(selectedMessages);
                      if (newSet.has(contextMenu.messageId)) newSet.delete(contextMenu.messageId);
                      else newSet.add(contextMenu.messageId);
                      setSelectedMessages(newSet);
                    }
                    setContextMenu(null);
                  }}
                >
                  {selectedMessages.has(contextMenu.messageId!) ? 'Deselect' : 'Select'}
                </button>
                <button
                  onClick={() => {
                    const id = contextMenu.messageId;
                    setContextMenu(null);
                    if (id) {
                      const msgs =
                        selectedMessages.size > 0 && selectedMessages.has(id)
                          ? Array.from(selectedMessages)
                          : [id];
                      handleBulkAction(showArchived ? 'unarchive' : 'archive', msgs);
                    }
                  }}
                >
                  {showArchived ? 'Unarchive' : 'Archive'}
                </button>
                <button
                  onClick={() => {
                    const id = contextMenu.messageId;
                    setContextMenu(null);
                    if (id) {
                      const msgs =
                        selectedMessages.size > 0 && selectedMessages.has(id)
                          ? Array.from(selectedMessages)
                          : [id];
                      handleBulkAction('read', msgs);
                    }
                  }}
                >
                  Mark as read
                </button>
                <button
                  className="danger"
                  onClick={() => {
                    const id = contextMenu.messageId;
                    setContextMenu(null);
                    if (id) {
                      const msgs =
                        selectedMessages.size > 0 && selectedMessages.has(id)
                          ? Array.from(selectedMessages)
                          : [id];
                      handleBulkAction('delete', msgs);
                    }
                  }}
                >
                  Delete message
                </button>
              </div>
            )}

            <div className="composer">
              <textarea
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                placeholder="Text message"
                rows={1}
                aria-label="Message"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={sendingMessage || !composerText.trim()}
                title="Send SMS"
              >
                {sendingMessage ? '…' : '➤'}
              </button>
            </div>
          </>
        ) : !selectedDevice ? (
          <div className="empty-state">
            {devices.length === 0 ? (
              <div className="getting-started">
                <h3>Pair your first phone</h3>
                <ol>
                  <li>Install Velinko on your Android phone from Google Play.</li>
                  <li>
                    Choose <strong>+ Pair</strong> on the left to get a 6-digit code.
                  </li>
                  <li>Type the code in the app and allow the SMS permissions.</li>
                </ol>
                <p>
                  On Samsung, also set the app&apos;s battery use to <strong>Unrestricted</strong>.{' '}
                  <a href="/docs#samsung">Why and how</a>
                </p>
                <button className="btn btn-primary" onClick={handlePairDevice}>
                  + Pair a phone
                </button>
              </div>
            ) : (
              <>
                <h3>No conversation selected</h3>
                <p>
                  Pick a device, then a conversation. OTP codes show up in the list so you can copy
                  them straight away.
                </p>
              </>
            )}
          </div>
        ) : null}
      </main>

      {toastMessage && <div className={`toast ${toastMessage.type}`}>{toastMessage.text}</div>}
    </div>
  );
}

export default DashboardPage;
